import { FunctionComponent, lazy, Suspense } from 'react';
import { useTheme } from '@mui/material';
import { Theme } from '@monorepo/client-types';
import { DesignIcon } from '@monorepo/nova-icons';
import { useBreakpoints } from '@monorepo/nova-ui';
import { Application } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { EnvService } from '../../services/core/env-service';

import './logo.scss';

const TtpDesktopDarkLogo = lazy(
  () => import('./ttp-logo-desktop-dark.svg?react'),
);
const TtpDesktopLightLogo = lazy(
  () => import('./ttp-logo-desktop-light.svg?react'),
);

const TtpCollapsedDarkLogo = lazy(
  () => import('./ttp-logo-collapsed-dark.svg?react'),
);
const TtpCollapsedLightLogo = lazy(
  () => import('./ttp-logo-collapsed-light.svg?react'),
);
const TtpNovaWhiteLogo = lazy(() => import('./ttp-logo-white-nova.svg?react'));

const The5ersDarkLogo = lazy(() => import('./5ers-logo-dark.svg?react'));
const The5ersLightLogo = lazy(() => import('./5ers-logo-light.svg?react'));
const The5ersCollapsedDarkLogo = lazy(
  () => import('./5ers-logo-collapsed-dark.svg?react'),
);
const The5ersCollapsedLightLogo = lazy(
  () => import('./5ers-logo-collapsed-light.svg?react'),
);

const The5ersNovaWhiteLogo = lazy(
  () => import('./5ers-logo-white-nova-2026.svg?react'),
);

const The5ersNovaLogoWithDate = lazy(
  () => import('./5ers-logo-nova-2025-with-date.svg?react'),
);

const project = EnvService.application;

interface Props {
  mode?: Theme;
}

const Logo: FunctionComponent<Props> = ({ mode }) => {
  const { themeStore, uiStore } = useStore();
  const currentTheme = mode || themeStore.currentTheme;

  const LogoMap: Record<
    Application,
    Record<
      'light' | 'dark',
      { regular: FunctionComponent; collapsed: FunctionComponent }
    >
  > = {
    [Application.TTP]: {
      light: {
        regular: TtpDesktopLightLogo,
        collapsed: TtpCollapsedLightLogo,
      },
      dark: {
        regular: TtpDesktopDarkLogo,
        collapsed: TtpCollapsedDarkLogo,
      },
    },
    [Application.THE5ERS]: {
      light: {
        regular: The5ersLightLogo,
        collapsed: The5ersCollapsedLightLogo,
      },
      dark: {
        regular: The5ersDarkLogo,
        collapsed: The5ersCollapsedDarkLogo,
      },
    },
  };

  const Component =
    LogoMap[project][currentTheme][
      uiStore.isMenuCollapsed ? 'collapsed' : 'regular'
    ];

  return (
    <Suspense>
      <div className="logo">
        <Component />
      </div>
    </Suspense>
  );
};

const NovaLogo = () => {
  return (
    <Suspense>
      {project === Application.TTP ? (
        <TtpNovaWhiteLogo height={25} />
      ) : (
        <The5ersNovaWhiteLogo height={52} />
      )}
    </Suspense>
  );
};

const NovaLogoWithDate = () => {
  const { isMobile } = useBreakpoints();
  const theme = useTheme();
  return (
    <Suspense>
      {project === Application.TTP ? (
        <DesignIcon
          iconName="TTPLogoMonoColor"
          height={isMobile ? theme.spacing(6.5) : theme.spacing(10)}
          color={theme.palette.text.main}
          useMuiSvgIcon={false}
        />
      ) : (
        <The5ersNovaLogoWithDate height={isMobile ? 67 : 88} />
      )}
    </Suspense>
  );
};
export default Logo;
export { NovaLogo, NovaLogoWithDate };
